/**
 * Éditeur dédié pour la page d'accueil
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notify } from '../components/NotificationContainer';
import api from '../services/api';
import logger from '../utils/logger';

const EditeurPageAccueil = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: 'Page d\'accueil',
    html_content: '',
    css_content: '',
    js_content: '',
    is_active: true
  });

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadHomePage();
  }, [user, navigate]);

  const loadHomePage = async () => {
    try {
      setLoading(true);

      // CORRECTION: Utiliser l'endpoint dédié by_url pour charger la page "/"
      try {
        const response = await api.get('/custom-pages/by_url/', {
          params: { url_path: '/' }
        });

        // La page existe, charger ses détails complets
        setEditingPage(response.data);
        setFormData({
          title: response.data.title,
          html_content: response.data.html_content || '',
          css_content: response.data.css_content || '',
          js_content: response.data.js_content || '',
          is_active: response.data.is_active
        });
        logger.log('[EditeurPageAccueil] Page "/" chargée avec succès, ID:', response.data.id);
      } catch (error) {
        // La page n'existe pas (404), charger le contenu par défaut pour création
        if (error.response?.status === 404) {
          logger.log('[EditeurPageAccueil] Page "/" non trouvée, chargement du contenu par défaut');
          const defaultResponse = await api.get('/custom-pages/get_default_homepage/');
          setFormData({
            title: 'Page d\'accueil',
            html_content: defaultResponse.data.html_content || '',
            css_content: defaultResponse.data.css_content || '',
            js_content: defaultResponse.data.js_content || '',
            is_active: true
          });
          setEditingPage(null); // Pas de page existante
        } else {
          throw error; // Autre erreur, on la propage
        }
      }
    } catch (error) {
      logger.error('Erreur lors du chargement:', error);
      notify.error('Erreur lors du chargement de la page d\'accueil');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      logger.log('[EditeurPageAccueil] Début de la sauvegarde, editingPage:', editingPage);

      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('url_path', '/');
      formDataToSend.append('content_source', 'editor');
      formDataToSend.append('is_active', formData.is_active);
      formDataToSend.append('html_content', formData.html_content);

      // Convertir le CSS en fichier si présent
      if (formData.css_content) {
        const cssBlob = new Blob([formData.css_content], { type: 'text/css' });
        formDataToSend.append('css_file', cssBlob, 'styles.css');
      }

      // Convertir le JS en fichier si présent
      if (formData.js_content) {
        const jsBlob = new Blob([formData.js_content], { type: 'text/javascript' });
        formDataToSend.append('js_file', jsBlob, 'script.js');
      }

      if (editingPage) {
        logger.log('[EditeurPageAccueil] Mise à jour de la page existante, ID:', editingPage.id);
        await api.patch(`/custom-pages/${editingPage.id}/`, formDataToSend);
        notify.success('Page d\'accueil modifiée avec succès ! Rafraîchissez l\'onglet de prévisualisation.');
      } else {
        logger.log('[EditeurPageAccueil] Création d\'une nouvelle page');
        // Lors de la création, initialiser la navbar avec les valeurs par défaut
        const defaultResponse = await api.get('/custom-pages/get_default_homepage/');
        formDataToSend.append('navbar_html', defaultResponse.data.navbar_html || '');
        formDataToSend.append('navbar_css', defaultResponse.data.navbar_css || '');
        formDataToSend.append('navbar_js', defaultResponse.data.navbar_js || '');

        // Activer include_navbar et include_footer par défaut
        formDataToSend.append('include_navbar', true);
        formDataToSend.append('include_footer', true);

        const response = await api.post('/custom-pages/', formDataToSend);
        setEditingPage(response.data);
        notify.success('Page d\'accueil créée avec succès ! Rafraîchissez l\'onglet de prévisualisation.');
      }
    } catch (error) {
      logger.error('[EditeurPageAccueil] Erreur lors de la sauvegarde:', error);
      logger.error('[EditeurPageAccueil] Détails de l\'erreur:', error.response?.data);
      notify.error(error.response?.data?.message || error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open('/', '_blank');
  };

  const handleDebug = () => {
    logger.log('=== DEBUG ÉDITEUR ===');
    logger.log('HTML (longueur):', formData.html_content.length);
    logger.log('HTML (300 premiers caractères):', formData.html_content.substring(0, 300));
    logger.log('CSS (longueur):', formData.css_content.length);
    logger.log('CSS (200 premiers caractères):', formData.css_content.substring(0, 200));
    logger.log('JS (longueur):', formData.js_content.length);
    logger.log('JS (200 premiers caractères):', formData.js_content.substring(0, 200));
    notify.success('Informations de debug affichées dans la console (F12)');
  };

  const handleReloadDefault = async () => {
    if (!window.confirm('Voulez-vous vraiment recharger le contenu par défaut ? Cela écrasera vos modifications actuelles.')) {
      return;
    }

    try {
      const defaultResponse = await api.get('/custom-pages/get_default_homepage/');
      setFormData({
        ...formData,
        html_content: defaultResponse.data.html_content || '',
        css_content: defaultResponse.data.css_content || '',
        js_content: defaultResponse.data.js_content || ''
      });
      notify.success('Contenu par défaut rechargé ! N\'oubliez pas d\'enregistrer.');
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du chargement du contenu par défaut');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Éditeur de la page d'accueil</h1>
            <p className="mt-2 text-gray-600">
              Modifiez le HTML, CSS et JavaScript de votre page d'accueil
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/admin/pages')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Retour
            </button>
            <button
              onClick={handleDebug}
              className="inline-flex items-center px-4 py-2 border border-yellow-300 rounded-md shadow-sm text-sm font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Debug
            </button>
            <button
              onClick={handleReloadDefault}
              className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Recharger par défaut
            </button>
            <button
              onClick={handlePreview}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Prévisualiser
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enregistrement...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>

        {/* Éditeurs */}
        <div className="space-y-6">
          {/* Titre */}
          <div className="bg-white shadow rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre de la page
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Éditeur HTML */}
          <div className="bg-white shadow rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code HTML
            </label>
            <textarea
              value={formData.html_content}
              onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
              rows={25}
              placeholder="<div>Votre code HTML ici...</div>"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              style={{ resize: 'vertical' }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Modifiez le code HTML de votre page d'accueil
            </p>
          </div>

          {/* Éditeur CSS */}
          <div className="bg-white shadow rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code CSS
            </label>
            <textarea
              value={formData.css_content}
              onChange={(e) => setFormData({ ...formData, css_content: e.target.value })}
              rows={25}
              placeholder="/* Votre CSS ici */
.ma-classe {
  color: blue;
}"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              style={{ resize: 'vertical' }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Styles CSS pour personnaliser l'apparence de votre page
            </p>
          </div>

          {/* Éditeur JavaScript */}
          <div className="bg-white shadow rounded-lg p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code JavaScript
            </label>
            <textarea
              value={formData.js_content}
              onChange={(e) => setFormData({ ...formData, js_content: e.target.value })}
              rows={25}
              placeholder="// Votre JavaScript ici
document.addEventListener('DOMContentLoaded', function() {
  logger.log('Page chargée !');
});"
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              style={{ resize: 'vertical' }}
            />
            <p className="text-xs text-gray-500 mt-2">
              Code JavaScript pour ajouter des interactions à votre page
            </p>
          </div>

          {/* Statut */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Page active (décocher pour revenir à la page par défaut)
              </label>
            </div>
          </div>
        </div>

        {/* Boutons du bas */}
        <div className="mt-8 flex justify-end space-x-3">
          <button
            onClick={() => navigate('/admin/pages')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditeurPageAccueil;
